import React, { useState, useMemo } from 'react';
import { useAppSelector, useAppDispatch } from '../../../store/hooks';
import { validateAndSetStep } from '../../../store/slices/bookSlice';
import { Menu, X } from 'lucide-react';

interface Step {
  id: string;
  label: string;
}

const allSteps: Step[] = [
  { id: 'book-info', label: 'Book Info' },
  { id: 'book-form-factor', label: 'Book Form Factor' },
  { id: 'book-formatting', label: 'Book Formatting' },
  { id: 'book-cover', label: 'Book Cover' },
  { id: 'pricing', label: 'Pricing' }
];

const Header: React.FC = () => {
  const dispatch = useAppDispatch();
  const currentStep = useAppSelector((state) => state.book.currentStep);
  const bookStatus = useAppSelector((state) => state.book.bookStatus);
  const isStep1Complete = useAppSelector((state) => state.book.isStep1Complete);
  const isStep2Complete = useAppSelector((state) => state.book.isStep2Complete);
  const isStep3Complete = useAppSelector((state) => state.book.isStep3Complete);
  const isStep4Complete = useAppSelector((state) => state.book.isStep4Complete);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // For rejected books being resubmitted, hide the pricing tab
  const isResubmission = bookStatus === 'rejected';
  const steps = useMemo(() => {
    if (isResubmission) {
      return allSteps.filter(step => step.id !== 'pricing');
    }
    return allSteps;
  }, [isResubmission]);

  // Determine which steps are accessible
  const isStepAccessible = (stepId: string): boolean => {
    const stepRequirements: { [key: string]: boolean } = {
      'book-info': true,
      'book-form-factor': isStep1Complete,
      'book-formatting': isStep1Complete && isStep2Complete,
      'book-cover': isStep1Complete && isStep2Complete && isStep3Complete,
      'pricing': isStep1Complete && isStep2Complete && isStep3Complete && isStep4Complete
    };
    return stepRequirements[stepId] || false;
  };

  const handleStepClick = (stepId: string) => {
    dispatch(validateAndSetStep(stepId));
  };

  return (
    <div className="bg-white border-b border-gray-200" style={{ position: 'relative', top: '-2px' }}>
      {/* Mobile Menu Toggle */}
      <div className="md:hidden flex items-center px-4 py-2">
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-1 text-gray-600"
        >
          {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
        <span className="ml-2 text-sm text-gray-700 font-medium">
          {steps.find(s => s.id === currentStep)?.label || 'Steps'}
        </span>
      </div>

      {/* Desktop Navigation Tabs */}
      <div className="hidden md:block px-6 pb-px">
        <div className="flex gap-2">
          {steps.map((step) => {
            const isAccessible = isStepAccessible(step.id);
            return (
              <button
                key={step.id}
                onClick={() => handleStepClick(step.id)}
                disabled={!isAccessible}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  currentStep === step.id
                    ? 'bg-orange-500 text-white rounded-t'
                    : isAccessible
                    ? 'text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-t'
                    : 'text-gray-400 cursor-not-allowed rounded-t opacity-50'
                }`}
                title={!isAccessible ? 'Complete previous steps to unlock' : ''}
              >
                {step.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Mobile Dropdown Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-200">
          <div className="px-4 py-2">
            {steps.map((step) => {
              const isAccessible = isStepAccessible(step.id);
              return (
                <button
                  key={step.id}
                  onClick={() => {
                    handleStepClick(step.id);
                    setIsMobileMenuOpen(false);
                  }}
                  disabled={!isAccessible}
                  className={`w-full text-left px-4 py-3 text-sm font-medium transition-colors mb-2 ${
                    currentStep === step.id
                      ? 'bg-orange-500 text-white rounded-md'
                      : isAccessible
                      ? 'text-gray-700 hover:bg-gray-100 rounded-md'
                      : 'text-gray-400 cursor-not-allowed rounded-md opacity-50'
                  }`}
                >
                  {step.label}
                  {!isAccessible && (
                    <span className="block text-xs text-gray-400 mt-1">
                      Complete previous steps first
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default Header;